import React, { useState } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import CenterModal from "@ats/src/components/modals/CenterModal";
import Button from "@ats/src/components/shared/Button";
import FormCheckbox from "@ats/src/components/forms/FormCheckbox";
import CopyField from "@ats/src/components/shared/CopyField";
import { CopyToClipboard } from "react-copy-to-clipboard";

import { useModalContext } from "@shared/context/ModalContext";
import { useToastContext } from "@shared/context/ToastContext";

interface Props {
  apiKey: string;
  onCancel: () => void;
}

function ApiKeyCreatedModal(props: Props) {
  const { apiKey, onCancel } = props;
  const { dismissModalWithAnimation } = useModalContext();
  const addToast = useToastContext();

  const [hasSavedKey, setHasSavedKey] = useState(false);

  const handleCopyKey = () => {
    addToast({ title: "Copied", kind: "success" });
  };

  const handleDownloadKey = () => {
    const blob = new Blob([apiKey], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "api_key.txt";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    dismissModalWithAnimation(() => onCancel);
  };

  const handleCheckboxChange = (name: string, value: boolean) => {
    setHasSavedKey(value);
  };

  return (
    <CenterModal headerTitleText="API Key Generated" onCancel={handleClose} hasUnsavedChanges={true}>
      <Styled.Warning>
        This is the only time you'll see this key. Copy or download it now.
      </Styled.Warning>

      <CopyField value={apiKey} label="Copy API key" />

      <Styled.Actions>
        <CopyToClipboard text={apiKey} onCopy={handleCopyKey}>
          <Button styleType="secondary">
            Copy to clipboard
          </Button>
        </CopyToClipboard>
        <Button styleType="secondary" onClick={handleDownloadKey}>
          Download as file
        </Button>
      </Styled.Actions>

      <Styled.Confirmation>
        <FormCheckbox
          name="hasSavedKey"
          label="I have saved my API key"
          checked={hasSavedKey}
          onChange={handleCheckboxChange}
        />
      </Styled.Confirmation>

      <Styled.CloseButton>
        <Button
          styleType="primary"
          onClick={handleClose}
          disabled={!hasSavedKey}
        >
          Close
        </Button>
      </Styled.CloseButton>
    </CenterModal>
  );
}

export default ApiKeyCreatedModal;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Warning = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: ApiKeyCreatedModal_Warning;
    ${[t.mb(5)]}
  `;
});

Styled.Actions = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: ApiKeyCreatedModal_Actions;
    ${[t.mb(5)]}
    display: flex;
    gap: 0.75rem;
  `;
});

Styled.Confirmation = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: ApiKeyCreatedModal_Confirmation;
    ${[t.mb(5)]}
  `;
});

Styled.CloseButton = styled.div((props) => {
  return css`
    label: ApiKeyCreatedModal_CloseButton;
  `;
});
